import { 
  ref, 
  push, 
  set, 
  get, 
  update, 
  remove,
  onValue,
  off,
  serverTimestamp
} from "firebase/database";
import { realtimeDb } from "./config";

// Create a new application entry
export const createApplication = async (applicationData) => {
  try {
    const applicationsRef = ref(realtimeDb, 'applications');
    const newApplicationRef = push(applicationsRef);
    
    const applicationWithTimestamp = {
      ...applicationData,
      id: newApplicationRef.key,
      createdAt: serverTimestamp(),
      status: 'pending'
    };
    
    await set(newApplicationRef, applicationWithTimestamp);
    
    return { 
      id: newApplicationRef.key, 
      data: applicationWithTimestamp,
      error: null 
    };
  } catch (error) {
    return { id: null, data: null, error: error.message };
  }
};

// Get all applications
export const getAllApplications = async () => {
  try {
    const applicationsRef = ref(realtimeDb, 'applications');
    const snapshot = await get(applicationsRef);
    
    if (snapshot.exists()) {
      const applications = [];
      snapshot.forEach((childSnapshot) => {
        applications.push({
          id: childSnapshot.key,
          ...childSnapshot.val()
        });
      });
      return { data: applications, error: null };
    } else {
      return { data: [], error: null };
    }
  } catch (error) {
    return { data: [], error: error.message };
  }
};

// Get applications by type
export const getApplicationsByType = async (applicationType) => {
  try {
    const applicationsRef = ref(realtimeDb, 'applications');
    const snapshot = await get(applicationsRef);
    
    if (snapshot.exists()) {
      const applications = [];
      snapshot.forEach((childSnapshot) => {
        const application = childSnapshot.val();
        if (application.applicationType === applicationType) {
          applications.push({
            id: childSnapshot.key,
            ...application
          });
        }
      });
      return { data: applications, error: null };
    } else {
      return { data: [], error: null };
    }
  } catch (error) {
    return { data: [], error: error.message };
  }
};

// Update application status
export const updateApplicationStatus = async (applicationId, status) => {
  try {
    const applicationRef = ref(realtimeDb, `applications/${applicationId}`);
    await update(applicationRef, { 
      status,
      updatedAt: serverTimestamp()
    });
    return { error: null };
  } catch (error) {
    return { error: error.message };
  }
};

// Delete an application
export const deleteApplication = async (applicationId) => {
  try {
    const applicationRef = ref(realtimeDb, `applications/${applicationId}`);
    await remove(applicationRef);
    return { error: null };
  } catch (error) {
    return { error: error.message };
  }
};

// Real-time listener for applications
export const listenToApplications = (callback) => {
  const applicationsRef = ref(realtimeDb, 'applications');
  
  const listener = onValue(applicationsRef, (snapshot) => {
    if (snapshot.exists()) {
      const applications = [];
      snapshot.forEach((childSnapshot) => {
        applications.push({
          id: childSnapshot.key,
          ...childSnapshot.val()
        });
      });
      callback(applications);
    } else {
      callback([]);
    }
  });
  
  // Return unsubscribe function
  return () => off(applicationsRef, 'value', listener);
};

// Real-time listener for specific application type
export const listenToApplicationsByType = (applicationType, callback) => {
  const applicationsRef = ref(realtimeDb, 'applications');
  
  const listener = onValue(applicationsRef, (snapshot) => {
    if (snapshot.exists()) {
      const applications = [];
      snapshot.forEach((childSnapshot) => {
        const application = childSnapshot.val();
        if (application.applicationType === applicationType) {
          applications.push({
            id: childSnapshot.key,
            ...application
          });
        }
      });
      callback(applications);
    } else {
      callback([]);
    }
  });
  
  // Return unsubscribe function
  return () => off(applicationsRef, 'value', listener);
};

// Get application by ID
export const getApplicationById = async (applicationId) => {
  try {
    const applicationRef = ref(realtimeDb, `applications/${applicationId}`);
    const snapshot = await get(applicationRef);
    
    if (snapshot.exists()) {
      return { 
        data: { 
          id: snapshot.key, 
          ...snapshot.val() 
        }, 
        error: null 
      };
    } else {
      return { data: null, error: "Application not found" };
    }
  } catch (error) {
    return { data: null, error: error.message };
  }
};

// Create a WhatsApp signup entry
export const createWhatsappSignup = async (signupData) => {
  try {
    const signupsRef = ref(realtimeDb, 'whatsapp_signups');
    const newSignupRef = push(signupsRef);
    
    const signupWithTimestamp = {
      ...signupData,
      id: newSignupRef.key,
      createdAt: serverTimestamp()
    };
    
    await set(newSignupRef, signupWithTimestamp);
    
    return { 
      id: newSignupRef.key, 
      data: signupWithTimestamp,
      error: null 
    };
  } catch (error) {
    return { id: null, data: null, error: error.message };
  }
}; 